import React, { useEffect, useState, useRef } from "react";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, StatusBadge, EmptyState } from "@/components/ui-parts";
import { RefreshCw, Loader2, GitCompareArrows, AlertTriangle, FileSignature, Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function Matching() {
  const [data, setData] = useState({ unmatched_transactions: [], unmatched_receipts: [], contract_candidates: [] });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [uploadTx, setUploadTx] = useState(null); // transaction that seeds the modal
  const [uploadForm, setUploadForm] = useState({ name: "", category: "Sonstiges", amount: "",
    amount_tolerance_down: "1", amount_tolerance_up: "50", frequency: "monthly",
    day_of_period: "", direction: "outgoing", counterparty_pattern: "",
    start_date: "", notes: "" });
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rep, comps] = await Promise.all([
        api.get("/analytics/missing"),
        api.get("/companies"),
      ]);
      setData({ contract_candidates: [], ...rep.data });
      setCompanies(comps.data);
    } catch { toast.error("Konnte nicht laden"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runReconcile = async () => {
    setRunning(true);
    try {
      const { data: start } = await api.post("/reconcile", {});
      if (start.status !== "running" || !start.run_id) {
        // Legacy synchronous response — treat matched as done.
        toast.success(`Abgleich beendet · ${start.matched || 0} neue Zuordnungen`);
        load();
        return;
      }
      // Poll status until the background job reports "done".
      const runId = start.run_id;
      let attempts = 0;
      const maxAttempts = 240; // 240 × 2 s = 8 min max, well beyond any realistic reconcile
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/reconcile/${runId}`);
          if (job.status === "done") {
            const parts = [`${job.matched || 0} neue Zuordnungen`];
            if (job.errors) parts.push(`${job.errors} Fehler`);
            toast.success(`Abgleich beendet · ${parts.join(" · ")}`);
            load();
            return true;
          }
          if (job.current_company) {
            toast.info(`Abgleich läuft · ${job.processed_companies || 0}/${job.total_companies || "?"} Firmen · aktuell: ${job.current_company}`,
              { id: "reconcile-progress", duration: 3000 });
          }
        } catch (err) {
          // transient — keep polling
        }
        return false;
      };
      while (attempts < maxAttempts) {
        attempts += 1;
        await new Promise((r) => setTimeout(r, 2000));
        if (await poll()) return;
      }
      toast.warning("Abgleich läuft weiter im Hintergrund — Seite neu laden für Ergebnis");
    } catch { toast.error("Abgleich fehlgeschlagen"); }
    finally { setRunning(false); }
  };

  const assign = async (txId, receiptId) => {
    try {
      await api.post("/match", { transaction_id: txId, receipt_id: receiptId });
      toast.success("Zugeordnet");
      load();
    } catch { toast.error("Zuordnung fehlgeschlagen"); }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const openUpload = (tx) => {
    // Prefill the contract-upload form from the transaction. The user just
    // picks a file + tweaks name; everything else is educated-guessed.
    const amount = Math.abs(Number(tx.amount) || 0);
    const dayOfMonth = tx.booking_date ? new Date(tx.booking_date).getDate() : "";
    // Derive a sensible contract name: counterparty > first significant word
    let name = tx.counterparty || "";
    if (!name && tx.description) {
      name = String(tx.description).split(/[\s\-·,]+/).slice(0, 3).join(" ").slice(0, 50);
    }
    setUploadTx(tx);
    setUploadForm({
      name: name || "Neuer Vertrag",
      category: "Sonstiges",
      amount: amount ? amount.toFixed(2) : "",
      amount_tolerance_down: "1",
      amount_tolerance_up: "50",
      frequency: "monthly",
      day_of_period: String(dayOfMonth || ""),
      direction: (Number(tx.amount) || 0) < 0 ? "outgoing" : "incoming",
      counterparty_pattern: (tx.counterparty || tx.description || "").slice(0, 60),
      start_date: tx.booking_date ? String(tx.booking_date).slice(0, 10) : "",
      notes: "",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submitUpload = async () => {
    if (!uploadTx) return;
    if (!uploadForm.name.trim() || !uploadForm.amount) {
      toast.error("Bitte Name und Betrag ausfüllen");
      return;
    }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("company_id", uploadTx.company_id || "");
      fd.append("name", uploadForm.name.trim());
      fd.append("category", uploadForm.category || "Sonstiges");
      fd.append("amount", uploadForm.amount);
      fd.append("amount_tolerance_down", uploadForm.amount_tolerance_down || "1");
      fd.append("amount_tolerance_up", uploadForm.amount_tolerance_up || "50");
      fd.append("frequency", uploadForm.frequency);
      if (uploadForm.day_of_period) fd.append("day_of_period", uploadForm.day_of_period);
      fd.append("direction", uploadForm.direction);
      if (uploadForm.counterparty_pattern.trim())
        fd.append("counterparty_pattern", uploadForm.counterparty_pattern.trim());
      if (uploadForm.start_date) fd.append("start_date", uploadForm.start_date);
      if (uploadForm.notes.trim()) fd.append("notes", uploadForm.notes.trim());
      const file = fileInputRef.current?.files?.[0];
      if (file) fd.append("file", file);
      await api.post("/contracts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Vertrag gespeichert · KI ordnet passende Buchungen jetzt zu");
      setUploadTx(null);
      // Trigger a reconcile so the newly uploaded contract picks up the tx
      try { await api.post("/reconcile", {}); } catch (_) {}
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setUploadBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Zuordnung"
        subtitle="Offene Buchungen und Belege manuell verbinden. Der automatische Abgleich läuft alle 2 Tage."
        actions={
          <PrimaryButton onClick={runReconcile} disabled={running} data-testid="run-matching-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" strokeWidth={1.75} />}
            KI-Abgleich starten
          </PrimaryButton>
        }
      />

      <div className="px-8 py-8 space-y-8">
        {loading ? (
          <div className="text-sm text-[#5C5E60]">Lade...</div>
        ) : (
          <>
            {data.contract_candidates && data.contract_candidates.length > 0 && (
              <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
                <div className="border-b border-[#E6E6E4] px-6 py-4 flex items-center justify-between bg-[#FFF7E6]">
                  <div className="flex items-start gap-3">
                    <FileSignature className="h-5 w-5 text-[#915000] mt-0.5" strokeWidth={1.75} />
                    <div>
                      <div className="font-display font-bold text-base text-[#1A1A1A]">Vermutlich Verträge</div>
                      <div className="text-xs text-[#5C5E60] mt-0.5">
                        Buchungen mit typischen Vertrags-Schlagworten (Miete, Rate, Abschlag, …) —
                        lad den passenden Vertrag hoch und die KI ordnet zukünftige Buchungen automatisch zu.
                      </div>
                    </div>
                  </div>
                  <StatusBadge variant="warning">{data.contract_candidates.length}</StatusBadge>
                </div>
                <div className="max-h-[420px] overflow-y-auto" data-testid="contract-candidates-list">
                  {data.contract_candidates.map((tx) => (
                    <div key={tx.id} className="px-6 py-3 border-b border-[#E6E6E4] hover:bg-[#F9F9F8] transition-colors duration-150 flex items-center gap-4"
                         data-testid={`contract-candidate-${tx.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-xs text-[#5C5E60]">
                          <span className="font-mono-tab">{fmtDate(tx.booking_date)}</span>
                          <span>·</span>
                          <span>{companyName(tx.company_id)}</span>
                        </div>
                        <div className="text-sm text-[#1A1A1A] mt-0.5 truncate">{tx.description}</div>
                        {tx.counterparty && (
                          <div className="text-xs text-[#5C5E60] mt-0.5 truncate">{tx.counterparty}</div>
                        )}
                      </div>
                      <div className={`text-sm font-mono-tab font-semibold shrink-0 ${tx.amount < 0 ? "text-[#B31D1D]" : "text-[#1E4620]"}`}>
                        {fmtEUR(tx.amount)}
                      </div>
                      <SecondaryButton onClick={() => openUpload(tx)}
                        data-testid={`upload-contract-${tx.id}`}
                        className="shrink-0">
                        <Upload className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Vertrag hochladen
                      </SecondaryButton>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Unmatched Transactions */}
            <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
              <div className="border-b border-[#E6E6E4] px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-base text-[#1A1A1A]">Buchungen ohne Beleg</div>
                  <div className="text-xs text-[#5C5E60] mt-0.5">Kontoauszug enthält, aber Beleg fehlt</div>
                </div>
                <StatusBadge variant={data.unmatched_transactions.length > 0 ? "warning" : "success"}>
                  {data.unmatched_transactions.length}
                </StatusBadge>
              </div>
              {data.unmatched_transactions.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#5C5E60]">Alle Buchungen haben einen Beleg.</div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto" data-testid="unmatched-tx-list">
                  {data.unmatched_transactions.map((tx) => (
                    <div key={tx.id} className="px-6 py-3 border-b border-[#E6E6E4] hover:bg-[#F9F9F8] transition-colors duration-150" data-testid={`match-tx-${tx.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-xs text-[#5C5E60]">
                            <span className="font-mono-tab">{fmtDate(tx.booking_date)}</span>
                            <span>·</span>
                            <span>{companyName(tx.company_id)}</span>
                          </div>
                          <div className="text-sm text-[#1A1A1A] mt-0.5 truncate">{tx.description}</div>
                        </div>
                        <div className={`text-sm font-mono-tab font-semibold shrink-0 ${tx.amount < 0 ? "text-[#B31D1D]" : "text-[#1E4620]"}`}>
                          {fmtEUR(tx.amount)}
                        </div>
                      </div>
                      <div className="mt-2">
                        <select
                          data-testid={`match-select-${tx.id}`}
                          defaultValue=""
                          onChange={(e) => e.target.value && assign(tx.id, e.target.value)}
                          className="w-full text-xs px-2 py-1 border border-[#E6E6E4] rounded-md bg-white hover:border-[#18322B]"
                        >
                          <option value="">Beleg zuordnen…</option>
                          {data.unmatched_receipts
                            .filter((r) => !tx.company_id || !r.company_id || r.company_id === tx.company_id)
                            .map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.vendor || r.filename} · {fmtEUR(r.total_amount)} · {fmtDate(r.date)}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Unmatched Receipts */}
            <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
              <div className="border-b border-[#E6E6E4] px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-base text-[#1A1A1A]">Belege ohne Buchung</div>
                  <div className="text-xs text-[#5C5E60] mt-0.5">Beleg vorhanden, aber keine Buchung gefunden</div>
                </div>
                <StatusBadge variant={data.unmatched_receipts.length > 0 ? "warning" : "success"}>
                  {data.unmatched_receipts.length}
                </StatusBadge>
              </div>
              {data.unmatched_receipts.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#5C5E60]">Alle Belege sind zugeordnet.</div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto" data-testid="unmatched-receipts-list">
                  {data.unmatched_receipts.map((r) => (
                    <div key={r.id} className="px-6 py-3 border-b border-[#E6E6E4] hover:bg-[#F9F9F8] transition-colors duration-150" data-testid={`match-rec-${r.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-xs text-[#5C5E60]">
                            <span className="font-mono-tab">{fmtDate(r.date)}</span>
                            <span>·</span>
                            <span>{r.company_id ? companyName(r.company_id) : "Keine Firma"}</span>
                          </div>
                          <div className="text-sm text-[#1A1A1A] mt-0.5 truncate">{r.vendor || r.filename}</div>
                          {r.summary && <div className="text-xs text-[#5C5E60] mt-0.5 truncate">{r.summary}</div>}
                        </div>
                        <div className="text-sm font-mono-tab font-semibold shrink-0">{fmtEUR(r.total_amount, r.currency)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </>
        )}
      </div>

      {uploadTx && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
             onClick={() => !uploadBusy && setUploadTx(null)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}
               data-testid="contract-upload-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-lg text-[#1A1A1A]">Vertrag hochladen</div>
                <div className="text-xs text-[#5C5E60] mt-0.5">
                  Für Buchung vom {fmtDate(uploadTx.booking_date)} · {fmtEUR(uploadTx.amount)}
                </div>
              </div>
              <button onClick={() => !uploadBusy && setUploadTx(null)} className="text-[#5C5E60] hover:text-[#1A1A1A]">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="text-xs text-[#5C5E60] bg-[#F9F9F8] rounded p-3 truncate">
              {uploadTx.description}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Bezeichnung *</label>
                <input value={uploadForm.name} onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                  data-testid="contract-name"
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Betrag € *</label>
                <input type="number" step="0.01" value={uploadForm.amount}
                  onChange={(e) => setUploadForm({ ...uploadForm, amount: e.target.value })}
                  data-testid="contract-amount"
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono-tab focus:outline-none focus:border-[#18322B]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Frequenz</label>
                <select value={uploadForm.frequency}
                  onChange={(e) => setUploadForm({ ...uploadForm, frequency: e.target.value })}
                  data-testid="contract-frequency"
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]">
                  <option value="monthly">monatlich</option>
                  <option value="quarterly">vierteljährlich</option>
                  <option value="yearly">jährlich</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Toleranz nach unten €</label>
                <input type="number" step="0.01" value={uploadForm.amount_tolerance_down}
                  onChange={(e) => setUploadForm({ ...uploadForm, amount_tolerance_down: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono-tab focus:outline-none focus:border-[#18322B]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Toleranz nach oben €</label>
                <input type="number" step="0.01" value={uploadForm.amount_tolerance_up}
                  onChange={(e) => setUploadForm({ ...uploadForm, amount_tolerance_up: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono-tab focus:outline-none focus:border-[#18322B]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Zahlungstag (Tag im Monat)</label>
                <input type="number" min="1" max="31" value={uploadForm.day_of_period}
                  onChange={(e) => setUploadForm({ ...uploadForm, day_of_period: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono-tab focus:outline-none focus:border-[#18322B]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Beginn Vertrag</label>
                <input type="date" value={uploadForm.start_date}
                  onChange={(e) => setUploadForm({ ...uploadForm, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Empfänger / Muster im Kontoauszug</label>
                <input value={uploadForm.counterparty_pattern}
                  onChange={(e) => setUploadForm({ ...uploadForm, counterparty_pattern: e.target.value })}
                  data-testid="contract-pattern"
                  placeholder="Wird zur Erkennung zukünftiger Buchungen genutzt"
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Vertragsdatei (PDF/Bild — optional)</label>
                <input type="file" ref={fileInputRef} accept="application/pdf,image/*"
                  data-testid="contract-file"
                  className="w-full text-xs" />
                <div className="text-[11px] text-[#5C5E60] mt-1">Die erste Seite erscheint im PDF-Export mit Vermerk „Vollständiger Vertrag liegt vor".</div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Notiz</label>
                <textarea rows={2} value={uploadForm.notes}
                  onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <SecondaryButton onClick={() => setUploadTx(null)} disabled={uploadBusy}>Abbrechen</SecondaryButton>
              <PrimaryButton onClick={submitUpload} disabled={uploadBusy}
                data-testid="contract-submit">
                {uploadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
                Vertrag speichern
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
